import { DeliveryStatus } from "@types_def/models/delivery.types";
import { generateDeliveryReport } from "@/api/Rest/delivery/generate-delivery-report";
import { downloadBuffer } from "@/utils/download";
import { getS3Url } from "@/utils/getData";

export type DeliveryReportDownloadOutcome = "downloaded" | "generating" | "not-ready" | "error";

type DeliveryReportReadinessInput = {
  status: string;
  vehicles?: Array<{ imagesBefore?: unknown; imagesAfter?: unknown }>;
};

export function getDeliveryReportS3Key(deliveryId: string | number) {
  return `deliveryDocs/delivery-${deliveryId}/rapport.pdf`;
}

export function getDeliveryReportFilename(deliveryId: string | number) {
  return `rapport-etat-livraison-${deliveryId}.pdf`;
}

export function getDeliveryReportUrl(deliveryId: string | number) {
  return getS3Url(getDeliveryReportS3Key(deliveryId));
}

export async function fetchDeliveryReportBuffer(
  deliveryId: string | number
): Promise<ArrayBuffer | null> {
  const url = getDeliveryReportUrl(deliveryId);
  if (!url) {
    return null;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    return await response.arrayBuffer();
  } catch {
    return null;
  }
}

export function downloadDeliveryReportBuffer(buffer: ArrayBuffer, deliveryId: string | number) {
  downloadBuffer(buffer, getDeliveryReportFilename(deliveryId));
}

export function isDeliveryReportReady(delivery: DeliveryReportReadinessInput): boolean {
  if (delivery.status !== DeliveryStatus.COMPLETED) {
    return false;
  }

  const vehicles = delivery.vehicles ?? [];
  if (vehicles.length === 0) {
    return false;
  }

  return vehicles.every((vehicle) => {
    const hasPhotoFields = "imagesBefore" in vehicle || "imagesAfter" in vehicle;
    if (!hasPhotoFields) {
      return true;
    }

    return Boolean(vehicle.imagesBefore) && Boolean(vehicle.imagesAfter);
  });
}

export async function regenerateDeliveryReport(
  deliveryId: string | number
): Promise<Exclude<DeliveryReportDownloadOutcome, "downloaded" | "not-ready">> {
  try {
    await generateDeliveryReport(deliveryId);
    return "generating";
  } catch {
    return "error";
  }
}

export async function downloadOrGenerateDeliveryReport(
  deliveryId: string | number,
  ready: boolean
): Promise<DeliveryReportDownloadOutcome> {
  if (!ready) {
    return "not-ready";
  }

  const buffer = await fetchDeliveryReportBuffer(deliveryId);
  if (buffer) {
    downloadDeliveryReportBuffer(buffer, deliveryId);
    return "downloaded";
  }

  try {
    await generateDeliveryReport(deliveryId);
    return "generating";
  } catch {
    return "error";
  }
}

export type BulkDeliveryReportResult = {
  downloaded: number[];
  generating: number[];
  notReady: number[];
  failed: number[];
};

export async function downloadOrGenerateDeliveryReportsSequentially(
  deliveries: Array<DeliveryReportReadinessInput & { id: number }>
): Promise<BulkDeliveryReportResult> {
  const result: BulkDeliveryReportResult = {
    downloaded: [],
    generating: [],
    notReady: [],
    failed: [],
  };

  for (const delivery of deliveries) {
    const ready = isDeliveryReportReady(delivery);
    const outcome = await downloadOrGenerateDeliveryReport(delivery.id, ready);

    switch (outcome) {
      case "downloaded":
        result.downloaded.push(delivery.id);
        break;
      case "generating":
        result.generating.push(delivery.id);
        break;
      case "not-ready":
        result.notReady.push(delivery.id);
        break;
      case "error":
        result.failed.push(delivery.id);
        break;
    }
  }

  return result;
}
